import React, { useMemo, useRef } from 'react'
import { Card, Col, Input, List, Row } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { RequestTicket } from 'src/types'

import { ApplicationState } from 'src/store'
import { currencyMask } from 'src/components/Formik'
import { setFullRequest } from 'src/core/store/ducks/requests/actions'
import { TagTicketSituation } from 'src/components/molecules'
import { Text, Title } from 'src/components/Ant'
import Images from 'src/config/images'

import { UpdateETicketCodeRequest } from '../util'

type Props = {
  item: RequestTicket
  canEdit: boolean
}

function TicketItem({ canEdit, item }: Props) {
  const { requests } = useSelector((state: ApplicationState) => ({
    requests: state.requests
  }))

  const { t } = useTranslation()
  const refTimeout = useRef<NodeJS.Timeout>(null)
  const dispatch = useDispatch()

  const { eticket_code, request_quote_vehicle, situation } =
    item.request_ticket_vehicle_selected

  const imgCompany = useMemo(() => {
    switch (request_quote_vehicle.company.toLowerCase()) {
      case 'alamo':
        return Images.rentalCompanies.alamo
      case 'localiza':
        return Images.rentalCompanies.localiza
      case 'movida':
        return Images.rentalCompanies.movida
      case 'unidas':
        return Images.rentalCompanies.unidas
      default:
        return undefined
    }
  }, [request_quote_vehicle.company])

  const { request_standard_sharings } =
    requests.fullRequest?.request_standard_assignments

  return (
    <List.Item>
      <Card
        title={
          <Row gutter={24}>
            <Col
              span={8}
              style={{
                display: 'flex',
                flexDirection: 'column',
                fontSize: 12
              }}
            >
              <Text type="secondary">Consolidadora:</Text>
              <Text strong type="secondary">
                TREND
              </Text>
            </Col>
            <Col
              span={8}
              style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                fontSize: 12
              }}
            >
              {!!situation && <TagTicketSituation status={situation} />}
            </Col>
            <Col
              span={8}
              style={{
                alignItems: 'flex-end',
                display: 'flex',
                flexDirection: 'column',
                fontSize: 12
              }}
            >
              {situation === 'ISSUED' && (
                <Text
                  code
                  copyable={{ text: eticket_code }}
                  style={{ fontSize: '16px' }}
                >
                  {eticket_code}
                </Text>
              )}
              {item.status === 'EMISSION_PENDING' && (
                <Input
                  disabled={!canEdit}
                  size="small"
                  placeholder="Emissão"
                  defaultValue={
                    item.request_ticket_vehicle_selected.eticket_code
                  }
                  onChange={({ target: { value } }) => {
                    clearTimeout(refTimeout.current)
                    refTimeout.current = setTimeout(() => {
                      dispatch(
                        setFullRequest(
                          UpdateETicketCodeRequest({
                            requests: requests.fullRequest,
                            request_ticket_id: item.request_ticket_id,
                            value
                          })
                        )
                      )
                    }, 500)
                  }}
                />
              )}
            </Col>
          </Row>
        }
      >
        <>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
                marginRight: 16,
                width: 200
              }}
            >
              <img
                alt="logo"
                src={request_quote_vehicle.vehicle_image_url}
                style={{
                  maxHeight: 200,
                  maxWidth: 200,
                  minWidth: 150
                }}
              />
              <img
                alt="logo"
                src={imgCompany}
                style={{
                  left: 0,
                  maxHeight: 70,
                  maxWidth: 70,
                  position: 'absolute',
                  top: 0
                }}
              />
            </div>
            <div>
              <Title level={4}>{request_quote_vehicle.model}</Title>
              <Text
                colon
                strong
                title="web.services.vehicle.form.withdrawal.title"
              />
              <Text>
                {moment
                  .unix(request_quote_vehicle.withdrawal_date)
                  .utc()
                  .format('DD/MM/YYYY HH:mm')}
                {`, ${request_quote_vehicle.withdrawal_location}`}
              </Text>
              <br />
              <Text
                colon
                strong
                title="web.services.vehicle.form.devolution.title"
              />
              <Text>
                {moment
                  .unix(request_quote_vehicle.return_date)
                  .utc()
                  .format('DD/MM/YYYY HH:mm')}
                {`, ${request_quote_vehicle.return_location}`}
              </Text>
              <br />
              <br />
              {request_quote_vehicle.information && (
                <>
                  <Text>{`- ${request_quote_vehicle.information}`}</Text>
                  <br />
                </>
              )}
              {request_quote_vehicle.air_conditioning && (
                <>
                  <Text>{'- '}</Text>
                  <Text title="web.services.vehicle.form.air-conditioning" />
                </>
              )}
              <br />
              <Text>{`- ${request_quote_vehicle.gearbox}`}</Text>
            </div>
          </div>
          {!!request_standard_sharings && (
            <Row style={{ marginTop: 16 }}>
              {request_standard_sharings
                ?.filter(({ active }) => active)
                .map(request_standard_sharing => (
                  <Text
                    key={request_standard_sharing.request_standard_sharing_id}
                  >
                    <b>{`${t('web.resources.cost-centers.title')}: `}</b>
                    {`${request_standard_sharing.cost_center.code} - ${request_standard_sharing.cost_center.name}`}
                  </Text>
                ))}
            </Row>
          )}
          <Row
            gutter={8}
            style={{
              border: '1px dashed #e8e8e8',
              borderRadius: 4,
              marginTop: 24,
              padding: '12px 16px'
            }}
          >
            <Col offset={18} span={6}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Text
                  colon
                  strong
                  type="secondary"
                  title="web.agencies.issuances.form.total"
                />
                <Text strong style={{ fontSize: 16, marginTop: 0 }}>
                  {`BRL ${
                    currencyMask({
                      formatStr: true,
                      precision: 2,
                      value: request_quote_vehicle.total_value
                    }).maskedValue
                  }`}
                </Text>
              </div>
            </Col>
          </Row>
        </>
      </Card>
    </List.Item>
  )
}

export default TicketItem
